import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosProgressEvent } from "axios";
import { isEqual, mapValues } from "lodash";
import { useMemo, useRef } from "react";
import {
  LOB,
  UpdateAddresses,
  UpdateReport,
  addComment,
  addManualAddress,
  calculatePremium,
  createCRMContact,
  deleteAdditionalFile,
  fetchEmailThreads,
  generatePolicyNumbers,
  getGoogleGroupStats,
  getSovOverview,
  getAllSovPivots,
  getUpdatingReports,
  listAdditionalFiles,
  listCRMContacts,
  listReport,
  listReports,
  listUsers,
  loadAddressesForMap,
  searchAddresses,
  updateAddresses,
  updateCRMContact,
  updateReport,
  uploadAdditionalFile,
} from "../../api/routes";
import * as client from "../../client";
import { useClientId } from "../../ClientConfig";
import {
  applyOverridesToAddress,
  applyOverridesToMetadataReponse,
} from "./Conversion";

const useReportsInner = (lobs: LOB[]) =>
  useQuery({
    enabled: lobs.length > 0,
    queryKey: ["reports", JSON.stringify(lobs)],
    queryFn: () => listReports(lobs),
    // Refetch every 2 minutes to avoid really stale data showing on the dashboard
    refetchInterval: 120_000,
  });

export const useReports = (lobs: LOB[]) => useReportsInner(lobs);
export const useSafeReports = (lobs: LOB[]) => {
  const clientId = useClientId();
  const reports = useReportsInner(lobs);
  return useMemo(
    () => ({
      ...reports,
      data: reports.data
        ? reports.data.map((report) =>
            applyOverridesToMetadataReponse(report, clientId)
          )
        : reports.data,
    }),
    [reports.data]
  );
};

export const useReport = (reportId?: string) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["reports", reportId],
    queryFn: async () => {
      return reportId ? await listReport(reportId) : undefined;
    },
  });
};

export const useUpdateAddresses = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["update-addresses", reportId],
    mutationFn: async (update: UpdateAddresses) => {
      return await updateAddresses(reportId, update);
    },
    onSuccess: (data) => {
      // Invalidate report list data because updating addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: ["reports"] });

      const prev = queryClient.getQueryData<client.ReportResponse>([
        "reports",
        reportId,
      ]);

      const updates = Object.fromEntries(
        data.addresses.map((a) => [a.address_id, a])
      );
      if (prev) {
        queryClient.setQueryData<client.ReportResponse>(["reports", reportId], {
          ...prev,
          report_json: {
            ...prev.report_json,
            sovs: prev.report_json.sovs.map((sov) => ({
              ...sov,
              addresses: sov.addresses.map((a) => updates[a.address_id] ?? a),
            })),
          },
        });
      }
    },
  });
};

export const useAddManualAddress = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["add-manual-address", reportId],
    mutationFn: async (address: client.RawAddress) => {
      return await addManualAddress(reportId, address);
    },
    onSuccess: (data) => {
      // Invalidate report list data because adding addresses can affect TIV/other metadata shown in the list field.
      queryClient.invalidateQueries({ queryKey: ["reports"] });

      queryClient.setQueryData(["reports", reportId], data.report);
    },
  });
};

export const useCalculatedPremium = (reportId: string) => {
  return useQuery({
    queryKey: ["reports", reportId, "calculated-premium"],
    queryFn: () => calculatePremium(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useUpdateReport = (reportId: string) => {
  const key = ["reports", reportId];
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: key,
    mutationFn: async (update: UpdateReport) => {
      return await updateReport(reportId, update);
    },
    onSuccess: (data) => {
      // invalidate prefix matches against queryKeys
      // Because the dashboard computes things like enabled TIV from the report,
      // conservatively assume that any change to the report could affect the
      // list view.
      queryClient.invalidateQueries({
        queryKey: ["reports"],
        // This call will trigger refetch immediately, so we have to be sure not
        // to invalidate the response data we will save below.
        predicate: (query) => !isEqual(query.queryKey, ["reports", reportId]),
      });
      // Trigger a re-fetch of updating reports immediately
      queryClient.invalidateQueries({
        queryKey: ["updating-reports"],
      });

      queryClient.setQueryData(["reports", reportId], data.report);
    },
  });
};

export const useUsers = () => {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => listUsers(),
    // Re-use the result for at least 5 seconds (just avoid re-fetching during load of the page, etc.)
    staleTime: 5000,
  });
};

export const useLocalUser = () => {
  return useUsers().data?.current_user;
};

export const useUserById = (
  userId: string | undefined | null
): client.User | undefined => {
  const users = useUsers();
  if (!userId) return undefined;
  return (users?.data?.all_company_users ?? []).find((u) => u.id === userId);
};

export const useCRMContacts = () => {
  return useQuery({
    queryKey: ["crm-contacts"],
    queryFn: () => listCRMContacts(),
    // Keep data fresh for 5 minutes before considering a refetch
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes even when unused
    gcTime: 10 * 60 * 1000,
    // Don't refetch when window regains focus
    refetchOnWindowFocus: false,
  });
};

/**
 * Merge a single contact into the cached `crm-contacts` list.
 * - Replaces an existing contact with the same id.
 * - Prepends the contact when it wasn't present (default behavior).
 * - Returns the new `ListContactsResponse` shape.
 */
function mergeContactIntoCache(
  queryClient: QueryClient,
  contact: client.ReportContact
): void {
  queryClient.setQueryData(
    ["crm-contacts"],
    (prev: client.ListContactsResponse | undefined) => {
      const existing = prev?.contacts ?? [];
      const replaced = existing.map((c) => (c.id === contact.id ? contact : c));
      const has = replaced.some((c) => c.id === contact.id);

      const contacts: client.ReportContact[] = has
        ? replaced
        : [contact, ...replaced];

      return { ...(prev ?? {}), contacts };
    }
  );
}
export const useCreateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-create-contact"],
    mutationFn: async (body: client.CreateContactRequest) => {
      return await createCRMContact(body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      }
    },
  });
};

export const useUpdateCRMContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["crm-update-contact"],
    mutationFn: async ({
      id,
      body,
    }: {
      id: string;
      body: client.UpdateContactRequest;
    }) => {
      return await updateCRMContact(id, body);
    },
    onSuccess: (response) => {
      if (response && response.contact) {
        mergeContactIntoCache(queryClient, response.contact);
      } else {
        queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      }
      // Other reports linking to the updated contact will need to be refreshed,
      // so invalidate the list of all reports.
      queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
  });
};

export const useAddressSearch = (
  lobs: LOB[],
  query: string,
  terms: client.TermSearch[]
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["search-addresses", query, ...terms],
    queryFn: async () => {
      const response =
        query || terms.length > 0
          ? await searchAddresses(lobs, query, terms)
          : { by_report: {}, term_query: [] };

      return {
        by_report: mapValues(response.by_report, (addresses) =>
          addresses.map(applyOverridesToAddress)
        ),
      };
    },
  });
};

export const useAdditionalFiles = (
  reportId: string,
  enabled: boolean = true
) => {
  return useQuery({
    queryKey: ["report", "additional-files", reportId],
    queryFn: () => listAdditionalFiles(reportId),
    enabled,
  });
};

export const useUploadAdditionalFiles = (
  reportId: string,
  onProgress: (e: AxiosProgressEvent) => void,
  isScreenshot: boolean
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["report", "upload-additional-file", reportId],
    mutationFn: (files: File[]) =>
      uploadAdditionalFile(reportId, files, onProgress, isScreenshot),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["reports", reportId] });
      queryClient.setQueryData(
        ["report", "additional-files", reportId],
        response
      );
    },
  });
};

export const useDeleteAdditionalFile = (reportId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["report", "delete-additional-file", reportId],
    mutationFn: (names: string[]) => deleteAdditionalFile(reportId, names),
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["report", "additional-files", reportId],
        response
      );
    },
  });
};

export const useAddressesForMap = (lobs: LOB[]) => {
  return useQuery({
    queryKey: ["addresses-for-map", lobs],
    queryFn: () => loadAddressesForMap(lobs),
    refetchOnWindowFocus: false,
    staleTime: 3_600_000,
  });
};

export const useSovOverview = (reportId: string) => {
  return useQuery({
    queryKey: ["sov-overview", reportId],
    queryFn: () => getSovOverview(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useAllSovPivots = (reportId: string) => {
  return useQuery({
    queryKey: ["sov-pivots-all", reportId],
    queryFn: () => getAllSovPivots(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useAddComment = (reportId: string) => {
  const key = ["reports", reportId];
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: key,
    mutationFn: async (message: string) => {
      return await addComment(reportId, message);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["reports"],
        exact: true,
      });
      queryClient.setQueryData(["reports", reportId], data.report);
    },
  });
};

export const useUpdatingReports = () => {
  const queryClient = useQueryClient();
  const doneUpdates = useRef<Set<string>>(new Set());
  return useQuery({
    queryKey: ["updating-reports"],
    queryFn: async (): Promise<client.ListUpdatingReportsResponse> => {
      const response = await getUpdatingReports(
        new Date(performance.timeOrigin)
      );

      // If any report finishes, invalidate cached data for it.
      for (const update of response.updates) {
        if (update.outcome && !doneUpdates.current.has(update.update_id)) {
          doneUpdates.current.add(update.update_id);
          queryClient.invalidateQueries({
            queryKey: ["reports", update.report_id],
          });
        }
      }

      return response;
    },
    refetchInterval: 5_000,
    staleTime: 5_000,
  });
};

export const useThisReportIsUpdating = (
  reportId: string,
  integrationType?: string
): { updating: client.ReportBeingUpdated } | undefined => {
  const updating = useUpdatingReports().data?.updates.find(
    (u) =>
      u.report_id === reportId &&
      !u.outcome &&
      (!integrationType || u.integration_type === integrationType)
  );
  return useMemo(() => updating && { updating }, [updating]);
};

export const useAllUpdatesForReport = (
  reportId: string,
  integrationType?: string
): client.ReportBeingUpdated[] => {
  const updates = useUpdatingReports().data?.updates ?? [];
  return useMemo(
    () =>
      updates.filter(
        (u) =>
          u.report_id === reportId &&
          !u.outcome &&
          (!integrationType || u.integration_type === integrationType)
      ),
    [updates, reportId, integrationType]
  );
};

export const useCancelJobs = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["cancel-jobs"],
    mutationFn: async (updateIds: string[]) => {
      await Promise.all(
        updateIds.map((id) =>
          client.asgardCancelJobAsgardCancelJobPost({
            requestBody: { report_integration_id: id },
          })
        )
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["updating-reports"] });
    },
  });
};

export const useEmailThreads = (reportId: string, enabled: boolean = true) => {
  return useQuery({
    enabled: enabled,
    queryKey: ["email-threads", reportId],
    queryFn: () => fetchEmailThreads(reportId),
    refetchOnWindowFocus: false,
  });
};

export const useGeneratePolicyNumbers = (reportId: string) => {
  return useMutation({
    mutationKey: ["generate-policy-numbers", reportId],
    mutationFn: async () => {
      return await generatePolicyNumbers(reportId);
    },
  });
};

export const useGoogleGroupStats = (email: string | null) =>
  useQuery({
    enabled: !!email,
    queryKey: ["google-group-stats", email],
    queryFn: () => getGoogleGroupStats(email!),
    refetchOnWindowFocus: false,
  });
